if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();

        this.zoomButton = this.querySelector('.product-media-modal__zoom');
        this.fullscreenButton = this.querySelector('.product-media-modal__full-screen');
        this.handleFullscreenChange = this.handleFullscreenChange.bind(this);

        this.zoomButton?.addEventListener('pointerup', (event) => { event.stopPropagation(); });
        this.zoomButton?.addEventListener('click', () => this.toggleZoom());

        this.fullscreenButton?.addEventListener('pointerup', (event) => { event.stopPropagation(); });
        this.fullscreenButton?.addEventListener('click', () => this.toggleFullscreen());

        document.addEventListener('fullscreenchange', this.handleFullscreenChange);
        document.addEventListener('webkitfullscreenchange', this.handleFullscreenChange);

        if (!this.requestFullscreen && !this.webkitRequestFullscreen) {
          this.fullscreenButton?.setAttribute('hidden', '');
        }
      }

      get swiper() {
        return this.querySelector('.product-media-modal__main')?.swiper;
      }

      toggleZoom() {
        const swiper = this.swiper;

        if (!swiper?.zoom) return;

        const isZoomed = swiper.zoom.scale > 1;

        if (isZoomed) {
          swiper.zoom.out();
        } else {
          swiper.zoom.in(3);
        }

        this.zoomButton?.classList.toggle('is-active', !isZoomed);
      }

      get fullscreenElement() {
        return document.fullscreenElement || document.webkitFullscreenElement;
      }

      async toggleFullscreen() {
        try {
          if (this.fullscreenElement) {
            const exitFullscreen = document.exitFullscreen || document.webkitExitFullscreen;
            await exitFullscreen?.call(document);
          } else {
            const requestFullscreen = this.requestFullscreen || this.webkitRequestFullscreen;
            await requestFullscreen?.call(this);
          }
        } catch (error) {
          console.error('Unable to toggle product modal fullscreen mode:', error);
        }
      }

      handleFullscreenChange() {
        const isFullscreen = this.fullscreenElement === this;

        this.fullscreenButton?.classList.toggle('is-active', isFullscreen);
        this.fullscreenButton?.setAttribute('aria-pressed', String(isFullscreen));
        this.toggleAttribute('data-fullscreen', isFullscreen);

        requestAnimationFrame(() => {
          this.swiper?.update();
          this.swiper?.thumbs?.swiper?.update();
        });
      }

      hide() {
        const swiper = this.swiper;

        const activeSlide =
          swiper?.slides?.[swiper.activeIndex] ||
          this.querySelector('.swiper-slide-active');

        const mediaId = activeSlide?.dataset.mediaId;

        swiper?.zoom?.out();
        this.zoomButton?.classList.remove('is-active');

        if (this.fullscreenElement === this) {
          const exitFullscreen = document.exitFullscreen || document.webkitExitFullscreen;
          exitFullscreen?.call(document);
        }

        super.hide();

        if (!mediaId) return;

        requestAnimationFrame(() => {
          const sectionId = this.dataset.section;
          const mediaGallery = document.getElementById(
            `MediaGallery-${sectionId}`
          );

          mediaGallery?.setActiveMedia(`${sectionId}-${mediaId}`, false);
        });
      }

      show(opener) {
        super.show(opener);

        requestAnimationFrame(() => {
          const swiper = this.swiper;

          if (!swiper) return;

          swiper.update();
          swiper.thumbs?.swiper?.update();
          this.showActiveMedia();

          if (!swiper.__modalZoomEventsAttached) {
            swiper.on('slideChangeTransitionStart', () => {
              swiper.zoom.out();
              this.zoomButton?.classList.remove('is-active');
            });
            swiper.on('slideChange', () => {
              this.syncGalleryWithActiveSlide();
            });

            swiper.__modalZoomEventsAttached = true;
          }
        });
      }

      showActiveMedia() {
        const swiper = this.swiper;
        const mediaId = this.openedBy?.getAttribute('data-media-id');

        if (!swiper || !mediaId) return;

        const activeIndex = Array.from(swiper.slides).findIndex(
          (slide) => slide.dataset.mediaId === mediaId
        );

        if (activeIndex < 0) return;

        swiper.slideTo(activeIndex, 0);

        const activeSlide = swiper.slides[activeIndex];
        const activeMedia = activeSlide?.querySelector('[data-media-id]');
        const activeMediaTemplate = activeMedia?.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;

        if (
          activeMedia?.nodeName == 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        )
          activeMedia.loadContent();
      }

      syncGalleryWithActiveSlide() {
        const swiper = this.swiper;
        if (!swiper) return;

        const activeSlide =
          swiper.slides?.[swiper.activeIndex] ||
          this.querySelector('.swiper-slide-active');

        const mediaId = activeSlide?.dataset.mediaId;

        if (!mediaId) return;

        const sectionId = this.dataset.section;
        const mediaGallery = document.getElementById(`MediaGallery-${sectionId}`);

        mediaGallery?.setActiveMedia(`${sectionId}-${mediaId}`, false);
      }

      disconnectedCallback() {
        document.removeEventListener('fullscreenchange', this.handleFullscreenChange);
        document.removeEventListener('webkitfullscreenchange', this.handleFullscreenChange);
      }
    }
  );
}
